import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../../config/api';
import './CustomerManagement.css';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterInsuranceType, setFilterInsuranceType] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterEmploymentStatus, setFilterEmploymentStatus] = useState('');


  const token = localStorage.getItem('token');

  // Form data for new/edit customer
  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    hasKids: false,
    kidsCount: '',
    kidsAges: '',
    currentInsurance: '',
    insuranceProvider: '',
    desiredCoverage: '',
    budgetRange: '',
    medicalHistory: '',
    medications: '',
    preferredDoctor: '',
    insuranceType: 'individual',
    planType: 'PPO',
    employmentStatus: '',
    annualIncome: '',
    policyStartDate: '',
    notes: '',
    leadId: '' // Link back to original lead
  });

  // Healthcare company options
  const healthcareProviders = [
    'Aetna', 'Anthem', 'Blue Cross Blue Shield', 'Cigna', 'Humana', 
    'UnitedHealthcare', 'Kaiser Permanente', 'Molina Healthcare', 
    'Centene', 'WellCare', 'Health Net', 'Independence Blue Cross',
    'Highmark', 'Oscar Health', 'Bright Health', 'Other'
  ];

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/api/admin/customers'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      } else {
        setError('Failed to load customers');
      }
    } catch (err) {
      setError('Error loading customers: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = selectedCustomer ? 'PUT' : 'POST';
      const url = selectedCustomer 
        ? getApiUrl(`/api/admin/customers/${selectedCustomer.customerId}`)
        : getApiUrl('/api/admin/customers');

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(customerForm)
      });

      if (response.ok) {
        await fetchCustomers();
        setShowAddCustomer(false);
        setSelectedCustomer(null);
        setCustomerForm({
          name: '', email: '', phone: '', age: '', hasKids: false,
          kidsCount: '', kidsAges: '', currentInsurance: '', desiredCoverage: '',
          budgetRange: '', medicalHistory: '', medications: '', preferredDoctor: '',
          insuranceType: 'individual', employmentStatus: '', annualIncome: '',
          policyStartDate: '', notes: '', leadId: ''
        });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save customer');
      }
    } catch (err) {
      setError('Error saving customer: ' + err.message);
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setCustomerForm(customer);
    setShowAddCustomer(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      const response = await fetch(getApiUrl(`/api/admin/customers/${customerId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchCustomers();
      } else {
        setError('Failed to delete customer');
      }
    } catch (err) {
      setError('Error deleting customer: ' + err.message);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.phone.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterInsuranceType || customer.insuranceType === filterInsuranceType;
    const matchesProvider = !filterProvider || customer.insuranceProvider.toLowerCase().includes(filterProvider.toLowerCase());
    const matchesEmploymentStatus = !filterEmploymentStatus || customer.employmentStatus === filterEmploymentStatus;
    return matchesSearch && matchesType && matchesProvider && matchesEmploymentStatus;
  });

  if (loading) {
    return (
      <div className="customer-loading">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading customers...</p>
      </div>
    );
  }

  return (
    <div className="customer-management-container">
      {/* Customer Management Header */}
      <div className="customer-management-header">
        <div>
          <h1 className="customer-management-title">Customer Management</h1>
          <p>Manage closed deals and client information</p>
        </div>
        <button
          onClick={fetchCustomers}
          className="refresh-button"
          disabled={loading}
        >
          ↻ Refresh
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')} className="error-close">&times;</button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Customers</h3>
          <div className="stat-number">{customers.length}</div>
          <div className="stat-label">Active Policies</div>
        </div>
        <div className="stat-card">
          <h3>Individual Plans</h3>
          <div className="stat-number">{customers.filter(c => c.insuranceType === 'individual').length}</div>
          <div className="stat-label">Customers</div>
        </div>
        <div className="stat-card">
          <h3>Family Plans</h3>
          <div className="stat-number">{customers.filter(c => c.insuranceType === 'family').length}</div>
          <div className="stat-label">Customers</div>
        </div>
        <div className="stat-card">
          <h3>This Month</h3>
          <div className="stat-number">{customers.filter(c => {
            const date = new Date(c.createdAt || Date.now());
            const now = new Date();
            return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
          }).length}</div>
          <div className="stat-label">New Customers</div>
        </div>
      </div>

      {/* Customers Section */}
      <div className="customers-section">
        <div className="section-header">
          <h2 className="section-title">Customer Directory</h2>
          <div className="section-actions">
            <div className="search-container">
              <span className="search-icon">⌕</span>
              <input
                type="text"
                className="search-input"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="filter-select"
              value={filterInsuranceType}
              onChange={(e) => setFilterInsuranceType(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="individual">Individual</option>
              <option value="family">Family</option>
              <option value="group">Group/Employer</option>
            </select>
            <select
              className="filter-select"
              value={filterProvider}
              onChange={(e) => setFilterProvider(e.target.value)}
            >
              <option value="">All Providers</option>
              {healthcareProviders.map(provider => (
                <option key={provider} value={provider}>{provider}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterEmploymentStatus}
              onChange={(e) => setFilterEmploymentStatus(e.target.value)}
            >
              <option value="">All Employment Statuses</option>
              <option value="employed">Employed</option>
              <option value="self-employed">Self-Employed</option>
              <option value="unemployed">Unemployed</option>
              <option value="retired">Retired</option>
              <option value="student">Student</option>
            </select>
            <button
              onClick={() => setShowAddCustomer(true)}
              className="add-customer-btn"
            >
              + Add Customer
            </button>
          </div>
        </div>

        {/* Customer Table */}
        {filteredCustomers.length === 0 ? (
          <div className="no-data">
            <p>No customers found</p>
            <span>Try adjusting your search or filters</span>
          </div>
        ) : (
          <div className="table-container">
            <table className="customers-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Age</th>
                  <th>Insurance Details</th>
                  <th>Provider</th>
                  <th>Policy Start</th>
                  <th>Family Info</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map(customer => (
                  <tr 
                    key={customer.customerId} 
                    className="customer-row"
                    onClick={() => handleEdit(customer)}
                  >
                    <td className="name-cell">{customer.name}</td>
                    <td>
                      <div className="email-cell">{customer.email}</div>
                      <div className="phone-cell">{customer.phone}</div>
                    </td>
                    <td>{customer.age}</td>
                    <td>
                      <div className="insurance-details">
                        <span className={`insurance-type ${customer.insuranceType}`}>
                          {customer.insuranceType}
                        </span>
                        <div className="plan-type">{customer.planType || 'PPO'}</div>
                      </div>
                    </td>
                    <td className="provider-cell">{customer.insuranceProvider || 'Not specified'}</td>
                    <td>{customer.policyStartDate || 'TBD'}</td>
                    <td>
                      {customer.hasKids ? (
                        <div>
                          <div>{customer.kidsCount} kids</div>
                          <div className="kids-ages">{customer.kidsAges}</div>
                        </div>
                      ) : (
                        'No children'
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(customer);
                          }}
                          className="edit-btn"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(customer.customerId);
                          }}
                          className="delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Customer Modal */}
      {showAddCustomer && (
        <div className="modal-overlay">
          <div className="modal-content customer-modal">
            <div className="modal-header">
              <h3>{selectedCustomer ? 'Edit Customer' : 'Add New Customer'}</h3>
              <button
                onClick={() => {
                  setShowAddCustomer(false);
                  setSelectedCustomer(null);
                  setCustomerForm({
                    name: '', email: '', phone: '', age: '', hasKids: false,
                    kidsCount: '', kidsAges: '', currentInsurance: '', desiredCoverage: '',
                    budgetRange: '', medicalHistory: '', medications: '', preferredDoctor: '',
                    insuranceType: 'individual', employmentStatus: '', annualIncome: '',
                    policyStartDate: '', notes: '', leadId: ''
                  });
                }}
                className="modal-close"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body customer-form">
              <div className="form-section">
                <h4>Basic Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={customerForm.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Age *</label>
                    <input
                      type="number"
                      name="age"
                      value={customerForm.age}
                      onChange={handleInputChange}
                      min="1"
                      max="120"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={customerForm.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={customerForm.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Family Information</h4>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="hasKids"
                      checked={customerForm.hasKids}
                      onChange={handleInputChange}
                    />
                    Has children
                  </label>
                </div>
                {customerForm.hasKids && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Number of children</label>
                      <input
                        type="number"
                        name="kidsCount"
                        value={customerForm.kidsCount}
                        onChange={handleInputChange}
                        min="1"
                        max="20"
                      />
                    </div>
                    <div className="form-group">
                      <label>Children's ages</label>
                      <input
                        type="text"
                        name="kidsAges"
                        value={customerForm.kidsAges}
                        onChange={handleInputChange}
                        placeholder="e.g., 5, 8, 12"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="form-section">
                <h4>Insurance Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Insurance Type *</label>
                    <select
                      name="insuranceType"
                      value={customerForm.insuranceType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="individual">Individual</option>
                      <option value="family">Family</option>
                      <option value="group">Group/Employer</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Plan Type *</label>
                    <select
                      name="planType"
                      value={customerForm.planType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="PPO">PPO (Preferred Provider Organization)</option>
                      <option value="HMO">HMO (Health Maintenance Organization)</option>
                      <option value="EPO">EPO (Exclusive Provider Organization)</option>
                      <option value="POS">POS (Point of Service)</option>
                      <option value="HDHP">HDHP (High Deductible Health Plan)</option>
                      <option value="Catastrophic">Catastrophic</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Insurance Provider *</label>
                    <select
                      name="insuranceProvider"
                      value={customerForm.insuranceProvider}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Provider</option>
                      {healthcareProviders.map(provider => (
                        <option key={provider} value={provider}>{provider}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Policy Start Date</label>
                    <input
                      type="date"
                      name="policyStartDate"
                      value={customerForm.policyStartDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Previous Insurance</label>
                    <input
                      type="text"
                      name="currentInsurance"
                      value={customerForm.currentInsurance}
                      onChange={handleInputChange}
                      placeholder="Previous provider/plan"
                    />
                  </div>
                  <div className="form-group">
                    <label>Budget Range</label>
                    <select
                      name="budgetRange"
                      value={customerForm.budgetRange}
                      onChange={handleInputChange}
                    >
                      <option value="">Select range</option>
                      <option value="$0-$200">$0 - $200</option>
                      <option value="$200-$400">$200 - $400</option>
                      <option value="$400-$600">$400 - $600</option>
                      <option value="$600-$800">$600 - $800</option>
                      <option value="$800+">$800+</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Employment & Income</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Employment Status</label>
                    <select
                      name="employmentStatus"
                      value={customerForm.employmentStatus}
                      onChange={handleInputChange}
                    >
                      <option value="">Select status</option>
                      <option value="employed">Employed</option>
                      <option value="self-employed">Self-Employed</option>
                      <option value="unemployed">Unemployed</option>
                      <option value="retired">Retired</option>
                      <option value="student">Student</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Annual Income Range</label>
                    <select
                      name="annualIncome"
                      value={customerForm.annualIncome}
                      onChange={handleInputChange}
                    >
                      <option value="">Select range</option>
                      <option value="$0-$25k">$0 - $25,000</option>
                      <option value="$25k-$50k">$25,000 - $50,000</option>
                      <option value="$50k-$75k">$50,000 - $75,000</option>
                      <option value="$75k-$100k">$75,000 - $100,000</option>
                      <option value="$100k+">$100,000+</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Medical Information</h4>
                <div className="form-group">
                  <label>Medical History</label>
                  <textarea
                    name="medicalHistory"
                    value={customerForm.medicalHistory}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Relevant medical history..."
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Current Medications</label>
                    <input
                      type="text"
                      name="medications"
                      value={customerForm.medications}
                      onChange={handleInputChange}
                      placeholder="List medications"
                    />
                  </div>
                  <div className="form-group">
                    <label>Preferred Doctor</label>
                    <input
                      type="text"
                      name="preferredDoctor"
                      value={customerForm.preferredDoctor}
                      onChange={handleInputChange}
                      placeholder="Doctor name"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Additional Notes</h4>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    value={customerForm.notes}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Additional notes about the customer..."
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowAddCustomer(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {selectedCustomer ? 'Update Customer' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement; 